import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { TIME_FORMAT } from 'common/constants/timeFormat'
import { useAppDispatch } from 'common/hooks/redux'
import { add, differenceInMinutes, format, isAfter } from 'date-fns'
import dayjs from 'dayjs'
import { UPDATE_INFO_DRAGGING } from 'features/Home/Book/state/slice/bookSlice'

import { snapToFiveMinutes } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/helpers'
import { safeRefreshScheduleEvents } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/safeRefreshScheduleEvents'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import { useCallback, useMemo, useState } from 'react'

type UseScheduleCalendarResponse = {
  awaitConfirmationEvent: any
  setAwaitConfirmationEvent: (event: any) => void
  handleScheduleChange: (data: any) => Promise<void>
  proceedToUpdateEvents: () => Promise<void>
  events: IPatientSchedule[]
  handleSelect: (props: any) => void
}

type UseScheduleCalendarProps = {
  dayInterval: { min: string; max: string }
  disabledEverything?: boolean
  weekDays?: Record<
    string,
    {
      time_slots: {
        id: number
        start_time: string
        end_time: string
      }[]
    }
  >
  formattedDate: string
  isUpdating: boolean
  schedules: IPatientSchedule[]
  timeZone: string
  scheduleObj: any
  prepopulateTimeOnClick: number
  handleUpdateEvents: (adjustedData: any, event: any, newDate?: string) => Promise<boolean>
  createCustomAppointment?: (start: Date, end: Date, resourceId: string) => void
}

export const useScheduleWeekCalendar = ({
  dayInterval,
  disabledEverything = false,
  weekDays = {},
  isUpdating,
  schedules,
  timeZone,
  scheduleObj,
  prepopulateTimeOnClick,
  createCustomAppointment,
  handleUpdateEvents,
}: UseScheduleCalendarProps): UseScheduleCalendarResponse => {
  const [awaitConfirmationEvent, setAwaitConfirmationEvent] = useState<any>(null)

  const dispatch = useAppDispatch()

  const { setNotification } = useNotification()

  const updateDragging = (value: boolean) => {
    dispatch(UPDATE_INFO_DRAGGING(value))
  }

  const shouldProceedToUpdate = ({
    resourceId,
    start,
    end,
  }: {
    resourceId: string
    start: any
    end: any
  }) => {
    const dayData = weekDays[resourceId]

    if (!dayData || !dayData.time_slots.length) {
      setNotification({
        title: 'Update schedule error',
        description: 'No available time slots for this date.',
        type: ALERT_CONSTANTS.ERROR,
      })
      return false
    }

    const mergeSlots = (slots: any[]) => {
      if (!slots.length) return []
      const sorted = [...slots].sort((a, b) => a.start_time.localeCompare(b.start_time))
      const merged = [sorted[0]]
      for (let i = 1; i < sorted.length; i++) {
        const prev = merged[merged.length - 1]
        if (prev.end_time >= sorted[i].start_time) {
          prev.end_time = prev.end_time > sorted[i].end_time ? prev.end_time : sorted[i].end_time
        } else {
          merged.push({ ...sorted[i] })
        }
      }
      return merged
    }

    const mergedSlots = mergeSlots(dayData.time_slots.map((slot) => ({ ...slot })))

    const getTimeString = (date: Date) =>
      `${date.getHours().toString().padStart(2, '0')}:${date
        .getMinutes()
        .toString()
        .padStart(2, '0')}:00`
    const startTimeStr = getTimeString(start)
    const endTimeStr = getTimeString(end)
    const normalizedStart = new Date(`${resourceId} ${startTimeStr}`)
    const normalizedEnd = new Date(`${resourceId} ${endTimeStr}`)

    const isWithinAvailableSlot = mergedSlots.some((slot: any) => {
      const slotStart = new Date(`${resourceId} ${slot.start_time}`)
      const slotEnd = new Date(`${resourceId} ${slot.end_time}`)
      return normalizedStart >= slotStart && normalizedEnd <= slotEnd
    })

    if (!isWithinAvailableSlot) {
      setNotification({
        title: 'Update schedule error',
        description: 'Appointment must be within available time slots.',
        type: ALERT_CONSTANTS.ERROR,
      })
      return false
    }

    return true
  }

  const revertScheduleChange = useCallback(() => {
    safeRefreshScheduleEvents(scheduleObj?.current)
  }, [scheduleObj])

  const handleScheduleChange = async (data: IPatientSchedule): Promise<void> => {
    updateDragging(false)

    if (isUpdating || disabledEverything) {
      revertScheduleChange()
      return
    }

    const start = snapToFiveMinutes(new Date(data.start_time))
    let end = snapToFiveMinutes(new Date(data.end_time))

    const durationInMinutes = differenceInMinutes(end, start)
    if (durationInMinutes < 5) {
      end = dayjs(end).add(5, 'minutes').toDate()
    }

    const resourceId = dayjs(start).format('YYYY-MM-DD')

    const shouldUpdateEvent = shouldProceedToUpdate({
      resourceId,
      start,
      end,
    })

    if (!shouldUpdateEvent) {
      revertScheduleChange()
      return
    }

    const adjustedData = {
      room: typeof data.room === 'string' ? data.room : (data.room as Record<string, any>)?.code,
      start_time: format(start, TIME_FORMAT.TIME_PICKER_HH_SS),
      end_time: format(end, TIME_FORMAT.TIME_PICKER_HH_SS),
      temperature_bypass: data.temperature_bypass,
      consultation_staff: data.consultation_staff,
      procedure_code: !data?.procedure?.isCustom ? data?.procedure?.code : null,
      procedure_description: data?.procedure?.description,
      category_type_code: data?.category?.code,
      title: data?.title,
      comments: data?.comments,
      punctuality_type_code: Number(data?.punctuality?.appointment?.code),
      punctuality_minutes: data?.punctuality?.appointment?.minutes,
    }

    try {
      await handleUpdateEvents(adjustedData, data, resourceId)
    } catch {
      revertScheduleChange()
    }
  }

  const proceedToUpdateEvents = useCallback(async () => {
    if (!awaitConfirmationEvent) {
      return
    }

    const { start, end, event } = awaitConfirmationEvent
    const adjustedData = {
      room: null,
      start_time: format(start, TIME_FORMAT.TIME_PICKER_HH_SS),
      end_time: format(end, TIME_FORMAT.TIME_PICKER_HH_SS),
      temperature_bypass: event.temperature_bypass,
      consultation_staff: event.consultation_staff,
      procedure_code: !event?.procedure?.isCustom ? event?.procedure?.code : null,
      procedure_description: event?.procedure?.description,
      category_type_code: event?.category?.code,
      title: event?.title,
      comments: event?.comments,
      punctuality_type_code: Number(event?.punctuality?.appointment?.code),
      punctuality_minutes: event?.punctuality?.appointment?.minutes,
    }

    const successUpdate = await handleUpdateEvents(adjustedData, event)
    if (!successUpdate) {
      return
    }

    scheduleObj?.current?.saveEvent({ ...event })
    setAwaitConfirmationEvent(null)
  }, [awaitConfirmationEvent, handleUpdateEvents])

  const eventsToShow = useMemo(() => {
    if (!schedules) {
      return []
    }

    return schedules.map((event) => {
      const startDateStr = `${event.date} ${event.start_time}`
      const endDateStr = `${event.date} ${event.end_time}`

      const startDate = dayjs.tz(startDateStr, timeZone).toDate()
      const endDate = dayjs.tz(endDateStr, timeZone).toDate()

      return {
        ...event,
        room_id: event.date, // Use date as resourceId for week view
        start_time: startDate,
        end_time: endDate,
        patient: {
          ...event.patient,
          name: !event.patient.name
            ? `${event.patient.last_name}, ${event.patient.first_name}`
            : event.patient.name,
        },
      }
    })
  }, [schedules, timeZone])

  const handleSelect = useCallback(
    (props: any) => {
      if (disabledEverything) {
        return
      }

      // In Week view, selection data might be different
      const cellData = props.data || props
      const startTime = cellData.startTime || cellData.start_time || cellData.StartTime
      const endTime = cellData.endTime || cellData.end_time || cellData.EndTime

      if (!startTime || !endTime) {
        return
      }

      const start_time = new Date(startTime)
      let end_time = new Date(endTime)

      // Get the date from the start_time for week view
      const dateKey = dayjs(start_time).format('YYYY-MM-DD')

      const diffInMinutes = differenceInMinutes(end_time, start_time)
      const prepPopulateTime = prepopulateTimeOnClick
      const updatedEnd = add(start_time, { minutes: prepPopulateTime })

      // Get available time slots for this date
      const dayData = weekDays[dateKey]
      const timeSlots = dayData?.time_slots || []

      // Find the next time slot end that would limit our appointment
      const nextSlotEnd = timeSlots.find((slot: any) => {
        const slotEnd = new Date(`${dateKey} ${slot.end_time}`)
        return isAfter(slotEnd, start_time) && isAfter(updatedEnd, slotEnd)
      })?.end_time

      const endDayDate = new Date(`${dateKey} ${dayInterval.max}`)

      const dateToCheck = nextSlotEnd ? new Date(`${dateKey} ${nextSlotEnd}`) : endDayDate

      if (diffInMinutes < prepPopulateTime && !isAfter(updatedEnd, dateToCheck)) {
        end_time = updatedEnd
      } else if (diffInMinutes < prepPopulateTime && isAfter(updatedEnd, dateToCheck)) {
        end_time = dateToCheck
      }

      createCustomAppointment(start_time, end_time, dateKey)
    },
    [disabledEverything, prepopulateTimeOnClick, weekDays, dayInterval, createCustomAppointment],
  )

  return {
    handleScheduleChange,
    events: eventsToShow,
    awaitConfirmationEvent,
    setAwaitConfirmationEvent,
    proceedToUpdateEvents,
    handleSelect,
  }
}
